import * as React from "react";
import { cn } from "../../lib/utils";
import { useNumberFormat } from "../../app/providers/NumberFormatProvider";

const paginationBtn =
  "px-2.5 py-1 border border-border rounded-md bg-white text-slate-600 cursor-pointer text-xs font-semibold hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed !mt-0";

function buildPageItems(page, pageCount) {
  return Array.from({ length: pageCount }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === pageCount || Math.abs(p - page) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);
}

/**
 * Controlled pagination bar shared by Staff / Targets lists.
 *
 * @param {number} page — 1-based current page
 * @param {number} pageCount — total pages
 * @param {(next: number) => void} onChange
 * @param {React.ReactNode} [summary] — optional range label (e.g. "نمایش 1–10 از 50")
 * @param {boolean} [showPageNumbers] — numbered buttons with ellipsis (Staff table); otherwise "page / total"
 */
function Pagination({
  page,
  pageCount,
  onChange,
  summary,
  showPageNumbers = false,
  className,
}) {
  const { formatInt } = useNumberFormat();
  const total = Math.max(1, pageCount || 1);
  const current = Math.min(Math.max(1, page || 1), total);
  const showControls = total > 1;

  if (summary == null && !showControls) return null;

  return (
    <div
      className={cn(
        "flex items-center gap-2 bg-white text-xs flex-wrap",
        summary != null
          ? "justify-between border-t border-muted px-4 py-2.5 text-slate-400"
          : "justify-center border-t border-muted py-2",
        className,
      )}
    >
      {summary != null && <span>{summary}</span>}
      {showControls && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={paginationBtn}
            disabled={current <= 1}
            onClick={() => onChange(current - 1)}
          >
            ‹ قبلی
          </button>

          {showPageNumbers
            ? buildPageItems(current, total).map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="text-slate-400">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={cn(
                      paginationBtn,
                      current === p &&
                        "border-primary bg-primary text-white",
                    )}
                    onClick={() => onChange(p)}
                  >
                    {formatInt(p)}
                  </button>
                ),
              )
            : (
              <span
                className={cn(
                  "text-xs text-muted-foreground",
                  summary != null && "px-2",
                )}
              >
                {formatInt(current)} / {formatInt(total)}
              </span>
            )}

          <button
            type="button"
            className={paginationBtn}
            disabled={current >= total}
            onClick={() => onChange(current + 1)}
          >
            بعدی ›
          </button>
        </div>
      )}
    </div>
  );
}

export { Pagination };
